import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';

import { Asset } from 'entities/assets/interfaces/asset';
import { AssetType } from 'entities/assets/model';
import { ImageReview } from 'entities/assets/ui/image-review';
import {
	GetAssetByIdDocument,
	GetAssetsDocument,
} from 'pages/art-catalog/ui/ArtCatalogTable/queries.gen';
import { useGetAssetError } from 'shared/hooks/asset-error';
import { useAppToast } from 'shared/hooks/toast';
import { InputField, InputFieldWithLink } from 'shared/ui';
import { FindOneClothesDocument } from 'widgets/update-asset-modal/ui/modal/queries.gen';

import { KeywordsPicker } from '../../keywords-picker';
import {
	updateClothesSchema,
	UpdateClothesValues,
} from '../lib/schemas/clothes';
import { useUpdateClothesMutation } from './graphql/queries.gen';

export interface Props {
	asset: Asset;
}

export const UpdateClothesForm: React.FC<Props> = ({ asset }) => {
	const toast = useAppToast();
	const handleError = useGetAssetError();
	const [isDisabledFilename, setIsDisabledFilename] = useState(
		!!asset.filename,
	);
	const [isUploadError, setIsUploadError] = useState(false);

	useEffect(() => {
		setIsDisabledFilename(!!asset.filename);
	}, [asset.filename]);

	const [updateClothes] = useUpdateClothesMutation({
		onCompleted: () => {
			toast({
				title: 'Clothes updated successfully',
				status: 'success',
			});
			setIsUploadError(false);
		},
		onError: (mutationError) => {
			handleError(mutationError.message, 'Clothes');
			setIsUploadError(!asset?.link);
		},
		refetchQueries: [
			GetAssetsDocument,
			FindOneClothesDocument,
			GetAssetByIdDocument,
		],
	});

	return (
		<Formik<UpdateClothesValues>
			initialValues={{
				id: asset.id,
				filename: asset.filename || '',
				link: asset.link,
				keywords: asset.keywords,
				newKeywords: asset.newKeywords,
				price: asset.price || 0,
				dropboxLink: asset.dropboxLink || '',
			}}
			validationSchema={updateClothesSchema}
			onSubmit={(value) => {
				updateClothes({
					variables: {
						clothes: {
							...value,
							price: parseInt(value.price as any, 10),
						},
					},
				});
			}}
			enableReinitialize
		>
			{({ values, submitForm, setFieldValue, errors, validateForm }) => (
				<Form>
					<InputField
						label="Filename"
						name="filename"
						value={values.filename?.trim() || ''}
						onApply={async (v) => {
							setFieldValue('filename', v, true);
							await validateForm();
							if (!errors.filename) submitForm();
						}}
						error={errors.filename}
						placeholder="cloths_f_regular_0.png"
						isDisabledFilename={isDisabledFilename}
					/>
					<ImageReview
						label="Clothes"
						type={asset.type}
						atName={isUploadError ? '' : asset?.atName?.trim() || ''}
						imageSrc={isUploadError ? '' : values.link?.trim() || ''}
						onImageChange={async (link) => {
							setFieldValue('link', link, true);
							await validateForm();
							return submitForm();
						}}
						isUploadError={isUploadError}
						error={errors.link}
					/>
					<KeywordsPicker
						type={AssetType.CLOTHES}
						selectedKeywords={values.newKeywords}
						onApply={async (v) => {
							setFieldValue('newKeywords', v, true);
							await validateForm();
							submitForm();
						}}
					/>
					<InputFieldWithLink
						label="Link"
						name="dropboxLink"
						value={values.dropboxLink?.trim() || ''}
						onApply={async (v: string | number) => {
							setFieldValue('dropboxLink', v, true);
							await validateForm();
							if (!errors.dropboxLink) submitForm();
						}}
						error={errors.dropboxLink}
						placeholder="https://..."
					/>
				</Form>
			)}
		</Formik>
	);
};
